import { useCallback, useEffect, useState } from "react";
import api, { inr, formatDate } from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import { CreditCard, RefreshCw, CheckCircle2, XCircle, AlertCircle, ExternalLink } from "lucide-react";

const EVENT_FILTERS = [
  { value: "", label: "All" },
  { value: "payment.captured", label: "Captured" },
  { value: "payment.authorized", label: "Authorized" },
  { value: "payment.failed", label: "Failed" },
  { value: "order.paid", label: "Order Paid" },
  { value: "payment.dispute.created", label: "Disputed" },
];

function EventBadge({ event }) {
  const e = (event || "").toLowerCase();
  if (e.includes("captured") || e.includes("paid")) {
    return <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-sm bg-olive/10 text-olive border border-olive/20"><CheckCircle2 size={10} /> {event}</span>;
  }
  if (e.includes("failed") || e.includes("dispute")) {
    return <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-sm bg-rose/10 text-rose border border-rose/20"><XCircle size={10} /> {event}</span>;
  }
  return <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-sm bg-gold/10 text-gold-deep border border-gold/20"><AlertCircle size={10} /> {event}</span>;
}

export default function AdminPayments() {
  const [data, setData] = useState({ events: [], totals_by_event: {}, total: 0 });
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(false);

  const refetch = useCallback(() => {
    // api, encodeURIComponent, and setState setters are stable — [filter] is the only real dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    setLoading(true);
    const q = filter ? `?event=${encodeURIComponent(filter)}` : "";
    api.get(`/admin/webhook-events${q}`).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [filter]);

  useEffect(() => { refetch(); }, [refetch]);

  const rzpLink = (pid) => pid ? `https://dashboard.razorpay.com/app/payments/${pid}` : null;

  return (
    <DashboardLayout
      title="Payments"
      subtitle={`${data.total} webhook events received · ${Object.keys(data.totals_by_event).length} event types`}
      actions={
        <button
          onClick={refetch}
          data-testid="payments-refresh"
          disabled={loading}
          className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest px-3 py-1.5 border border-navy/15 rounded-sm hover:bg-cream/40 text-navy/70 disabled:opacity-50"
        >
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      }
    >
      {/* Stat cards by event type */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5" data-testid="payments-stats">
        {EVENT_FILTERS.slice(1, 5).map((f) => {
          const count = data.totals_by_event[f.value] || 0;
          return (
            <button
              key={f.value}
              onClick={() => setFilter(filter === f.value ? "" : f.value)}
              data-testid={`payments-stat-${f.value.replace(/\./g, "-")}`}
              className={`bg-white border rounded-sm p-4 text-left transition-all hover:border-gold/40 ${filter === f.value ? "border-gold ring-1 ring-gold/30" : "border-navy/10"}`}
            >
              <div className="flex items-center justify-between">
                <CreditCard size={14} className="text-navy/40" />
                {filter === f.value && <span className="text-[9px] uppercase tracking-widest text-gold-deep">Filtered</span>}
              </div>
              <div className="text-[10px] uppercase tracking-widest text-navy/50 mt-2">{f.label}</div>
              <div className="font-serif text-2xl text-navy mt-1">{count}</div>
            </button>
          );
        })}
      </div>

      {/* Filter chips */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="payments-filters">
        {EVENT_FILTERS.map((f) => (
          <button
            key={f.value || "all"}
            onClick={() => setFilter(f.value)}
            data-testid={`payments-filter-${f.value || "all"}`}
            className={`text-[11px] uppercase tracking-widest px-3 py-1 rounded-full border transition-colors ${
              filter === f.value
                ? "bg-navy text-gold border-navy"
                : "bg-white text-navy/60 border-navy/15 hover:border-gold/40"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Events table */}
      <div className="bg-white border border-navy/10 rounded-sm overflow-hidden" data-testid="payments-table">
        <div className="px-5 py-3 border-b border-navy/10 flex items-center justify-between">
          <div className="font-serif text-base text-navy">Recent webhook events</div>
          <div className="text-xs text-navy/50">{data.events.length} shown</div>
        </div>
        {data.events.length === 0 ? (
          <div className="p-8 text-center text-sm text-navy/50">
            No events to display.
            <br />
            <span className="text-[11px]">Razorpay webhooks will appear here once payments are made.</span>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-navy/10 bg-cream/30 text-navy/60">
                  <th className="text-left px-4 py-2.5 font-medium text-[10px] uppercase tracking-widest">Event</th>
                  <th className="text-left px-4 py-2.5 font-medium text-[10px] uppercase tracking-widest">Payment ID</th>
                  <th className="text-right px-4 py-2.5 font-medium text-[10px] uppercase tracking-widest">Amount</th>
                  <th className="text-left px-4 py-2.5 font-medium text-[10px] uppercase tracking-widest">Method</th>
                  <th className="text-left px-4 py-2.5 font-medium text-[10px] uppercase tracking-widest">User</th>
                  <th className="text-left px-4 py-2.5 font-medium text-[10px] uppercase tracking-widest">Intent</th>
                  <th className="text-left px-4 py-2.5 font-medium text-[10px] uppercase tracking-widest">Order</th>
                  <th className="text-left px-4 py-2.5 font-medium text-[10px] uppercase tracking-widest">Received</th>
                </tr>
              </thead>
              <tbody>
                {data.events.map((e) => (
                  <tr key={e.id} data-testid={`payment-row-${e.id}`} className="border-b border-navy/5 last:border-0 hover:bg-cream/20">
                    <td className="px-4 py-2"><EventBadge event={e.event} /></td>
                    <td className="px-4 py-2 font-mono text-[11px]">
                      {e.payment_id ? (
                        <a
                          href={rzpLink(e.payment_id)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gold-deep hover:underline inline-flex items-center gap-1"
                          data-testid={`payment-link-${e.id}`}
                        >
                          {e.payment_id} <ExternalLink size={10} />
                        </a>
                      ) : (
                        <span className="text-navy/30">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums text-navy">{e.amount ? inr(e.amount) : "—"}</td>
                    <td className="px-4 py-2 text-navy/60 capitalize">{e.method || "—"}</td>
                    <td className="px-4 py-2 font-mono text-[11px] text-navy">{e.user_code || "—"}</td>
                    <td className="px-4 py-2 text-navy/60 capitalize text-xs">{e.intent || "—"}{e.package_code ? ` · ${e.package_code}` : ""}</td>
                    <td className="px-4 py-2">
                      {e.reconciled_order_no ? (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 bg-olive/10 text-olive rounded-sm border border-olive/20" data-testid={`payment-reconciled-${e.id}`}>
                          <CheckCircle2 size={10} /> {e.reconciled_order_no}
                        </span>
                      ) : (
                        <span className="text-[10px] uppercase tracking-widest text-navy/40">unmatched</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-[11px] text-navy/50">{formatDate(e.received_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
